import { TFunction } from "i18next";

/**
 * Por que fallo un sync, en palabras del usuario.
 *
 * Vive fuera de los componentes porque lo usan dos pantallas distintas -el
 * detalle del run en Datasets y la notificacion `dataset-sync-failure`- y
 * tienen que decir lo mismo del mismo error. Cuando cada una tenia su texto, la
 * notificacion mandaba a re-autorizar la cuenta para errores que no eran de la
 * cuenta (MXH-5913).
 *
 * Por eso tambien comparten la key de locize, `datasets:syncRuns.reason.<CODE>`,
 * en vez de tener una copia por namespace: dos traducciones del mismo codigo se
 * desincronizan en cuanto alguien edita una sola.
 */

const UNKNOWN_REASON = "The synchronization failed for an unknown reason";

/**
 * Los codigos que escribe el sweeper de MXH-5780 comparten mensaje a proposito.
 *
 * Describen infraestructura nuestra (la lambda murio, el re-encolado no
 * funciono) y no hay nada distinto que el usuario pueda hacer en un caso y en
 * el otro. Lo unico que necesita saber es que el run no termino, que el dataset
 * quedo sin actualizar y que puede volver a correrlo.
 *
 * Los codigos siguen siendo distintos en Couchbase y CloudWatch, que es donde
 * los usamos para medir: dejan de ser user-facing, no dejan de existir.
 */
const INTERRUPTED =
  "The synchronization did not finish, so the dataset was not updated. You can run it again.";

/**
 * Textos en ingles usados mientras la key no exista en locize.
 *
 * Son el piso, no el reemplazo: las traducciones a ja/fr (y el ajuste del
 * ingles) se cargan en locize y pisan esto. Pero garantizan que el motivo se
 * vea desde el primer deploy, que es lo que pidio MXH-5737.
 */
const REASON_FALLBACK: Record<string, string> = {
  STORAGE_AUTH_EXPIRED:
    "The cloud storage account is no longer authorized. Its credentials expired or were revoked.",
  STORAGE_ACCESS_DENIED:
    "The cloud storage account does not have permission to read the selected folder.",
  STORAGE_ITEM_NOT_FOUND:
    "The selected folder no longer exists in the cloud storage.",
  RATE_LIMIT_EXCEEDED:
    "The cloud storage is rejecting requests for exceeding its rate limit.",
  STORAGE_UNAVAILABLE:
    "The cloud storage returned an error. It may be a temporary problem on their side.",
  TIMEOUT: "The synchronization ran out of time before finishing.",
  // Sin numeros a proposito: el backend manda el tope en el mensaje crudo, que
  // queda en "ver detalles". Aca importa que la salida es elegir otra carpeta.
  DATASET_TOO_LARGE:
    "The selected folder has too many items to synchronize. Select a more specific folder.",
  MAX_RETRIES_EXCEEDED:
    "The synchronization failed after exhausting every retry.",
  STUCK_IN_PROCESSING: INTERRUPTED,
  STUCK_IN_WAITING_RETRY: INTERRUPTED,
  // Codigo del vocabulario anterior a MXH-5737. Sigue apareciendo en los runs ya
  // guardados, que viven 30 dias; sin esto muestran el crudo "Not found".
  NOT_FOUND: "The selected folder no longer exists in the cloud storage.",
  // UNKNOWN_ERROR queda fuera a proposito: ahi el mensaje crudo de la API es
  // mas util que cualquier frase generica que podamos escribir.
};

/**
 * `details` es el mensaje crudo de la API. Solo lo pasa el detalle del run, que
 * lo tiene y ademas lo muestra entero en "ver detalles"; la notificacion no lo
 * recibe a proposito, porque tambien sale por mail y ahi no hay donde
 * contenerlo.
 */
export default function syncErrorReason(
  t: TFunction,
  code: string,
  details?: string | null
) {
  return t(`datasets:syncRuns.reason.${code}`, {
    defaultValue: REASON_FALLBACK[code] || details || UNKNOWN_REASON,
  });
}
